import React, { useState, useEffect } from 'react';
import { useAlert } from '../context/AlertContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Plus, Trash2, Edit, Search, User, ChevronRight } from 'lucide-react';
import { Table, Button, InputGroup, Form, Card, Row, Col, Image } from 'react-bootstrap';
import './UserList.css';

interface UserData {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    photo?: string;
}

const UserList = () => {
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const [users, setUsers] = useState<UserData[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await api.get('/company/users/');
            setUsers(response.data);
        } catch (error) {
            console.error('Error fetching users:', error);
            showAlert('Erro ao carregar usuários.', { type: 'error' });
        }
    };

    const handleDelete = (id: number) => {
        showAlert('Tem certeza que deseja excluir este usuário?', {
            title: 'Confirmação',
            confirmable: true,
            confirmText: 'Excluir',
            confirmVariant: 'btn-primary',
            onConfirm: async () => {
                try {
                    await api.delete(`/company/users/${id}/`);
                    fetchUsers();
                    showAlert('Usuário excluído com sucesso!', { type: 'success' });
                } catch (error) {
                    console.error('Error deleting user:', error);
                    showAlert('Erro ao excluir usuário.', { type: 'error' });
                }
            }
        });
    };

    const filteredUsers = users.filter(u =>
        u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (u.first_name + ' ' + u.last_name).toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Card className="shadow-sm border-0">
            <Card.Body>
                <Row className="mb-4 align-items-center">
                    <Col>
                        <h3 className="mb-0">Usuários</h3>
                    </Col>
                    <Col xs="auto">
                        <Button variant="success" onClick={() => navigate('/users/new')} className="d-flex align-items-center gap-2">
                            <Plus size={18} /> Novo Usuário
                        </Button>
                    </Col>
                </Row>

                <InputGroup className="mb-4">
                    <InputGroup.Text className="bg-white border-end-0">
                        <Search size={18} className="text-muted" />
                    </InputGroup.Text>
                    <Form.Control
                        placeholder="Pesquisar por nome, usuário ou e-mail..."
                        value={searchTerm}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                        className="border-start-0 ps-0"
                    />
                </InputGroup>

                {/* Desktop: Tabela */}
                <div className="custom-table-container d-none d-md-block">
                    <Table hover className="custom-table mb-0">
                        <thead>
                            <tr>
                                <th style={{ width: '80px' }}>Foto</th>
                                <th>Nome</th>
                                <th>Usuário</th>
                                <th>E-mail</th>
                                <th className="text-end">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.map((user) => (
                                <tr
                                    key={user.id}
                                    onClick={() => navigate(`/users/${user.id}`)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td className="align-middle">
                                        {user.photo ? (
                                            <Image
                                                src={user.photo}
                                                alt={`${user.first_name} ${user.last_name}`}
                                                roundedCircle
                                                style={{
                                                    width: '50px',
                                                    height: '50px',
                                                    objectFit: 'cover',
                                                    border: '2px solid #dee2e6'
                                                }}
                                            />
                                        ) : (
                                            <div
                                                className="d-flex align-items-center justify-content-center bg-light rounded-circle"
                                                style={{
                                                    width: '50px',
                                                    height: '50px',
                                                    border: '2px solid #dee2e6'
                                                }}
                                            >
                                                <User size={24} className="text-muted" />
                                            </div>
                                        )}
                                    </td>
                                    <td className="fw-medium align-middle">{user.first_name} {user.last_name}</td>
                                    <td className="align-middle">{user.username}</td>
                                    <td className="align-middle text-muted">{user.email}</td>
                                    <td className="text-end align-middle">
                                        <Button
                                            variant="link"
                                            className="text-secondary p-1 me-2"
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                navigate(`/users/${user.id}`);
                                            }}
                                        >
                                            <Edit size={18} />
                                        </Button>
                                        <Button
                                            variant="link"
                                            className="text-secondary p-1"
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                handleDelete(user.id);
                                            }}
                                        >
                                            <Trash2 size={18} />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {filteredUsers.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="text-center py-4 text-muted">
                                        Nenhum usuário encontrado.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>

                {/* Mobile: Lista de Cards */}
                <div className="user-list-mobile d-md-none">
                    {filteredUsers.length > 0 ? (
                        filteredUsers.map((user) => (
                            <div key={user.id} className="user-card-mobile">
                                <div 
                                    className="user-card-mobile-content"
                                    onClick={() => navigate(`/users/${user.id}`)}
                                >
                                    {user.photo ? (
                                        <Image
                                            src={user.photo}
                                            alt={`${user.first_name} ${user.last_name}`}
                                            roundedCircle
                                            width={48}
                                            height={48}
                                            style={{ objectFit: 'cover' }}
                                            className="user-card-mobile-photo"
                                        />
                                    ) : (
                                        <div className="user-card-mobile-photo-placeholder">
                                            <User size={28} className="text-secondary" />
                                        </div>
                                    )}
                                    <div className="user-card-mobile-info">
                                        <span className="user-card-mobile-name">{user.first_name} {user.last_name}</span>
                                        <span className="user-card-mobile-username">@{user.username}</span>
                                    </div>
                                    <ChevronRight size={20} className="text-muted" />
                                </div>
                                <div className="user-card-mobile-actions">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="user-card-mobile-btn"
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            navigate(`/users/${user.id}`);
                                        }}
                                    >
                                        <Edit size={16} /> Editar
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="user-card-mobile-btn"
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            handleDelete(user.id);
                                        }}
                                    >
                                        <Trash2 size={16} /> Excluir
                                    </Button>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-4 text-muted">
                            Nenhum usuário encontrado.
                        </div>
                    )}
                </div>
            </Card.Body>
        </Card>
    );
};

export default UserList;
